// src/component/OtohikariTest.js
// OTOHIKARIマップ 亜種テストページ
// 目的: 海岸線データを高解像度（Natural Earth 1:10m / land-10m.json）に差し替えて
//       粒度の違いを確認する。地球儀本体（OtohikariGlobe.js）は無改修で流用。
//
// 参考: https://onesea.vercel.app/mmm （land-10m.json + land-50m.json を使用）
// ルート: /otohikari-test

import React, { useEffect, useState } from "react";
import { Box, Typography, Chip, Stack, CircularProgress } from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../api/firebase";
import OtohikariGlobe from "./OtohikariGlobe";

// 海岸線データの解像度を切り替えて比較できるようにしておく
const ATLAS_OPTIONS = [
  { key: "10m", label: "1:10m（最高精細）", url: "/data/land-10m.json" },
  { key: "50m", label: "1:50m（中）", url: "/data/land-50m.json" },
  { key: "110m", label: "1:110m（現行）", url: "/data/land-110m.json" },
];

export default function OtohikariTest() {
  const [trackId, setTrackId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [atlasKey, setAtlasKey] = useState("10m");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "musicSettings", "topPageSeasonalTrack"));
        const settings = snap.exists() ? snap.data() : {};
        if (alive) setTrackId(settings.trackId || null);
      } catch (err) {
        console.error("[OtohikariTest] trackId load error:", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const atlas = ATLAS_OPTIONS.find((o) => o.key === atlasKey) || ATLAS_OPTIONS[0];

  return (
    <Box sx={{ bgcolor: "#01030a", minHeight: "100vh", color: "#fff", pb: 6 }}>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mx: "auto" }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: "0.2em", opacity: 0.6 }}
        >
          TEST · 海岸線 高解像度版
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
          OTOHIKARIマップ（亜種テスト）
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.6, mb: 2 }}>
          海岸線データの解像度だけを差し替えた比較用ページです。地球儀本体は本番と同じコンポーネントを使用しています。
        </Typography>

        {/* 解像度切り替え（比較用） */}
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
          {ATLAS_OPTIONS.map((o) => (
            <Chip
              key={o.key}
              label={o.label}
              onClick={() => setAtlasKey(o.key)}
              variant={o.key === atlasKey ? "filled" : "outlined"}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.3)",
                bgcolor: o.key === atlasKey ? "rgba(90,53,255,0.6)" : "transparent",
              }}
            />
          ))}
        </Stack>

        {loading ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: "#fff" }} />
          </Box>
        ) : (
          <Box sx={{ borderRadius: 3, overflow: "hidden" }}>
            {/* key に atlasKey を含めて解像度切替時に確実に再マウント */}
            <OtohikariGlobe
              key={atlas.key}
              height={520}
              autoRotate
              db={db}
              trackId={trackId}
              worldAtlasUrl={atlas.url}
              aggregateCollectionName="musicDailyAggregates"
            />
          </Box>
        )}

        <Typography variant="caption" sx={{ display: "block", mt: 2, opacity: 0.45 }}>
          データ: Natural Earth via world-atlas@2 / 現在: {atlas.label}
          {!trackId && "（trackId未取得: musicSettings/topPageSeasonalTrack を確認）"}
        </Typography>
      </Box>
    </Box>
  );
}
